import { Group, Loader, Text } from '@mantine/core';

import type { ArenaRun } from '../hooks/useModelArena';
import { progress, topBy } from '../runGrouping';
import { elapsedLabel, modelLabel } from '../runLabels';

import { readable } from './metricFormat';
import { displayOf } from './tableData';

/**
 * What the collapsed row says about a run: how far along if it is going, the leading candidate once
 * it has one, and why there is nothing to show otherwise. A headline rather than a verdict —
 * expanding sorts by whatever the reader actually cares about.
 */
export function RunHeadline({ run, rankMetric }: { run: ArenaRun; rankMetric: string | null }) {
  if (run.status === 'RUNNING') return <Measured run={run} />;

  const leading = topBy(run.results, rankMetric);
  if (leading === undefined) {
    return (
      <Text size="sm" c="dimmed" truncate>
        {run.status === 'QUEUED' ? 'Waiting for a worker' : 'No results'}
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
      <Text size="sm" truncate>
        {modelLabel(leading)}
      </Text>
      {rankMetric !== null && (
        <Text size="sm" ff="monospace">
          {readable(rankMetric)} {displayOf(leading.metrics[rankMetric])}
        </Text>
      )}
    </Group>
  );
}

/** Candidates finished rather than time spent: a run moves a whole candidate at a time, tens of
 * minutes each, so nothing creeps along in between. */
function Measured({ run }: { run: ArenaRun }) {
  const { measured, total } = progress(run);
  const elapsed = elapsedLabel(run, new Date());

  return (
    <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
      <Loader size={14} />
      <Text size="sm" c="dimmed">
        {measured} of {total} measured{elapsed && ` · ${elapsed}`}
      </Text>
    </Group>
  );
}
